import { Button, Input, Modal } from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { useDeferredValue, useMemo, useState } from 'react';
import { formatNumber } from '../lib/format';
import { useDeckTree } from '../lib/queries';
import type { SubtopicNode } from '../lib/types';
import { Carregando, Erro } from './estado';

/**
 * Navegação pela árvore de assuntos de uma matéria.
 *
 * A árvore de algumas matérias tem milhares de nós. Renderizar tudo de uma vez
 * congelava a página, por isso os ramos só montam quando são abertos e a busca
 * corre sobre a árvore achatada uma única vez por matéria.
 */
export function SeletorAssunto({
  aberto,
  deckId,
  deckNome,
  selecionado,
  aoFechar,
  aoEscolher,
}: {
  aberto: boolean;
  deckId: number;
  deckNome: string;
  selecionado: string | null;
  aoFechar: () => void;
  aoEscolher: (path: string | null) => void;
}): ReactNode {
  const { data: tree, isPending, error, refetch } = useDeckTree(aberto ? deckId : null);
  const [busca, setBusca] = useState('');
  const buscaAdiada = useDeferredValue(busca);

  const achatada = useMemo(() => (tree ? achatar(tree) : []), [tree]);

  const resultados = useMemo(() => {
    const termo = normalizar(buscaAdiada);
    if (termo.length < 2) return null;
    return achatada
      .filter((n) => normalizar(n.label).includes(termo))
      .slice(0, 80);
  }, [achatada, buscaAdiada]);

  return (
    <Modal
      open={aberto}
      onClose={aoFechar}
      title="Escolher assunto"
      description={deckNome}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              aoEscolher(null);
              aoFechar();
            }}
          >
            Estudar a matéria inteira
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Buscar assunto"
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Digite ao menos duas letras"
        />

        {isPending ? <Carregando /> : null}
        {error ? <Erro error={error} aoTentarNovamente={() => void refetch()} /> : null}

        <div className="max-h-[50vh] overflow-y-auto pr-1">
          {resultados ? (
            resultados.length === 0 ? (
              <p className="py-8 text-center text-sm text-mc-ink3">
                Nenhum assunto com esse nome.
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {resultados.map((no) => (
                  <li key={no.path}>
                    <BotaoAssunto
                      no={no}
                      ativo={no.path === selecionado}
                      caminho={no.trilha}
                      onClick={() => {
                        aoEscolher(no.path);
                        aoFechar();
                      }}
                    />
                  </li>
                ))}
              </ul>
            )
          ) : (
            <Ramos
              nos={tree ?? []}
              nivel={0}
              selecionado={selecionado}
              aoEscolher={(path) => {
                aoEscolher(path);
                aoFechar();
              }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

function Ramos({
  nos,
  nivel,
  selecionado,
  aoEscolher,
}: {
  nos: SubtopicNode[];
  nivel: number;
  selecionado: string | null;
  aoEscolher: (path: string) => void;
}): ReactNode {
  return (
    <ul className={nivel === 0 ? 'flex flex-col gap-1' : 'ml-4 flex flex-col gap-1'}>
      {nos.map((no) => (
        <Ramo key={no.path} no={no} nivel={nivel} selecionado={selecionado} aoEscolher={aoEscolher} />
      ))}
    </ul>
  );
}

function Ramo({
  no,
  nivel,
  selecionado,
  aoEscolher,
}: {
  no: SubtopicNode;
  nivel: number;
  selecionado: string | null;
  aoEscolher: (path: string) => void;
}): ReactNode {
  const [aberto, setAberto] = useState(false);
  const temFilhos = no.children.length > 0;

  return (
    <li>
      <div className="flex items-center gap-1">
        {temFilhos ? (
          <button
            type="button"
            onClick={() => setAberto((v) => !v)}
            aria-expanded={aberto}
            aria-label={aberto ? `Fechar ${no.label}` : `Abrir ${no.label}`}
            className="grid size-8 shrink-0 place-items-center rounded text-mc-ink3 hover:text-mc-accent"
          >
            <svg
              viewBox="0 0 12 12"
              className={`size-3 fill-none stroke-current stroke-[1.5] transition-transform ${
                aberto ? 'rotate-90' : ''
              }`}
            >
              <path d="M4 2l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : (
          <span className="size-8 shrink-0" aria-hidden="true" />
        )}

        <BotaoAssunto
          no={no}
          ativo={no.path === selecionado}
          onClick={() => aoEscolher(no.path)}
        />
      </div>

      {aberto && temFilhos ? (
        <Ramos
          nos={no.children}
          nivel={nivel + 1}
          selecionado={selecionado}
          aoEscolher={aoEscolher}
        />
      ) : null}
    </li>
  );
}

function BotaoAssunto({
  no,
  ativo,
  caminho,
  onClick,
}: {
  no: { label: string; count: number };
  ativo: boolean;
  caminho?: string;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[40px] w-full items-center justify-between gap-3 rounded-mc-field px-3 text-left text-sm transition-colors ${
        ativo
          ? 'bg-mc-accent/12 text-mc-accent ring-1 ring-mc-accent/40'
          : 'text-mc-ink2 hover:bg-mc-surface-hover hover:text-mc-ink'
      }`}
    >
      <span className="min-w-0">
        <span className="block truncate">{no.label}</span>
        {caminho ? (
          <span className="mc-mono block truncate normal-case tracking-normal">
            {caminho}
          </span>
        ) : null}
      </span>
      <span className="mc-mono shrink-0 tabular-nums">{formatNumber(no.count)}</span>
    </button>
  );
}

interface NoAchatado {
  path: string;
  label: string;
  count: number;
  trilha: string;
}

function achatar(nos: SubtopicNode[], prefixo: string[] = []): NoAchatado[] {
  const saida: NoAchatado[] = [];
  for (const no of nos) {
    saida.push({
      path: no.path,
      label: no.label,
      count: no.count,
      trilha: prefixo.join(' › '),
    });
    if (no.children.length > 0) {
      saida.push(...achatar(no.children, [...prefixo, no.label]));
    }
  }
  return saida;
}

function normalizar(valor: string): string {
  return valor
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
